import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, Eraser, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtCurrency, fmtDateLong, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const pdfUrl = (token) => `${process.env.REACT_APP_BACKEND_URL}/api/public/sign/${token}/pdf`;

const SignaturePad = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const used = useRef(false);
  useEffect(() => {
    const c = ref.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio; c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio); ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0f172a";
  }, []);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); ref.current.setPointerCapture(e.pointerId); drawing.current = true; const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) { return; } const ctx = ref.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); used.current = true; };
  const up = () => { if (drawing.current && used.current) { onChange(ref.current.toDataURL("image/png")); } drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); used.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} className="h-40 w-full touch-none rounded-lg border-2 border-dashed border-slate-300 bg-white" onPointerDown={down} onPointerMove={move}
        onPointerUp={up} onPointerLeave={up} data-testid="sign-pad" />
      <div className="mt-1 flex justify-between text-xs text-slate-500"><span>Firmi qui con il dito o con il mouse</span>
        <button type="button" onClick={clear} className="inline-flex items-center gap-1 hover:text-ink" data-testid="sign-pad-clear"><Eraser className="h-3.5 w-3.5" />Cancella</button></div>
    </div>
  );
};

const Summary = ({ d, token }) => {
  const q = d.quote;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="sign-summary">
      <p className="font-mono text-xs font-semibold text-slate-500">Offerta {q.number}{q.revision ? ` · rev.${q.revision}` : ""}</p>
      <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink sm:text-3xl" data-testid="sign-quote-title">{q.title}</h1>
      <p className="mt-1 text-sm text-slate-600">Per {d.company_name} · valida fino al {fmtDateLong(q.valid_until)}{d.requested_by ? ` · referente ${d.requested_by}` : ""}</p>
      <div className="mt-4 overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b text-left text-xs uppercase text-slate-500"><th className="py-2">Descrizione</th><th className="py-2 text-right">Q.tà</th><th className="py-2 text-right">Importo</th></tr></thead>
        <tbody>{q.lines.map((l) => <tr key={l.position} className="border-b border-slate-100"><td className="py-2 pr-3 text-slate-700">{l.description}</td>
          <td className="py-2 text-right text-slate-600">{l.quantity} {l.unit || ""}</td><td className="py-2 text-right font-medium text-ink">{fmtCurrency(l.net_amount)}</td></tr>)}</tbody></table></div>
      <div className="mt-3 flex flex-col items-end gap-0.5 text-sm" data-testid="sign-totals"><span className="text-slate-600">Imponibile {fmtCurrency(q.taxable)} · IVA {fmtCurrency(q.vat)}</span>
        <span className="text-lg font-bold text-ink">Totale {fmtCurrency(q.total)}</span></div>
      <Button asChild variant="outline" className="mt-4 gap-1"><a href={pdfUrl(token)} target="_blank" rel="noreferrer" data-testid="sign-open-pdf"><Download className="h-4 w-4" />Leggi l'offerta completa (PDF)</a></Button>
    </section>
  );
};

const SignForm = ({ d, token, onSigned, onPending }) => {
  const [name, setName] = useState(d.name || "");
  const [code, setCode] = useState("");
  const [sig, setSig] = useState(null);
  const [accept, setAccept] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const sendCode = async () => {
    setBusy(true);
    try { const r = (await api.post(`/public/sign/${token}/code`)).data; setSent(true); toast.success(`Codice inviato a ${r.email}`); if (r.demo_code) { setCode(r.demo_code); toast.info(`Demo: il codice è ${r.demo_code}`); } }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const sign = async () => {
    setBusy(true);
    try {
      const r = (await api.post(`/public/sign/${token}/sign`, { code, signer_name: name.trim(), signature: sig, accept }, { timeout: 60000 })).data;
      if (r.pending) { onPending(); } else { onSigned(); }
    } catch (e) {
      if (!e.response || e.response.status >= 500) { onPending(); } else { toast.error(errorMessage(e)); }
    } finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="sign-form">
      <h2 className="text-base font-bold text-ink md:text-lg">Firma l'offerta</h2>
      <div className="mt-4 space-y-4">
        <div className="space-y-1.5"><Label className="text-xs">Nome e cognome del firmatario</Label><Input maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="sign-name" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Codice di verifica</Label>
          <div className="flex gap-2"><Input inputMode="numeric" maxLength={6} placeholder="6 cifre" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} disabled={!sent} data-testid="sign-code" />
            <Button variant="outline" disabled={busy} onClick={sendCode} data-testid="sign-send-code">{sent ? "Invia di nuovo" : "Inviami il codice"}</Button></div>
          <p className="text-xs text-slate-500">Il codice arriva via email a {d.email} e vale 10 minuti.</p></div>
        <SignaturePad onChange={setSig} />
        <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" className="mt-0.5" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="sign-accept" />
          Dichiaro di aver letto l'offerta e le condizioni allegate e di accettarle.</label>
        <Button className="w-full" disabled={busy || !sent || code.length !== 6 || name.trim().length < 3 || !sig || !accept} onClick={sign} data-testid="sign-submit">{busy ? "Firma in corso…" : "Firma e accetta l'offerta"}</Button>
        <p className="flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5" />Registriamo data, ora, indirizzo IP e verifica email insieme alla firma.</p>
      </div>
    </section>
  );
};

const Done = ({ d, token }) => (
  <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="sign-done">
    <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
    <h2 className="mt-2 text-lg font-bold text-ink">Offerta firmata, grazie!</h2>
    <p className="mt-1 text-sm text-slate-600">{d.signer_name ? `Firmata da ${d.signer_name}` : "Firma registrata"}{d.signed_at ? ` il ${fmtDateTime(d.signed_at)}` : ""}. Il suo referente è stato avvisato.</p>
    <Button asChild variant="outline" className="mt-4 gap-1"><a href={pdfUrl(token)} target="_blank" rel="noreferrer" data-testid="sign-download-signed"><Download className="h-4 w-4" />Scarica la copia firmata</a></Button>
  </section>
);

const Waiting = () => (
  <section className="rounded-xl border border-slate-200 bg-white p-6 text-center" data-testid="sign-pending">
    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-brand-600" />
    <h2 className="mt-3 text-base font-bold text-ink">Stiamo registrando la firma…</h2>
    <p className="mt-1 text-sm text-slate-600">Può richiedere qualche secondo: non chiuda la pagina.</p>
  </section>
);

export default function SignPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [polling, setPolling] = useState(false);
  const load = () => api.get(`/public/sign/${token}`).then((r) => { setD(r.data); return r.data; }).catch((e) => setErr(errorMessage(e)));
  useEffect(() => { load().then((x) => { if (x?.status === "in_firma") { setPolling(true); } }); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!polling) { return undefined; }
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      const x = await load();
      if (!x || x.status !== "in_firma" || n > 80) {
        clearInterval(t); setPolling(false);
        if (x?.status === "in_attesa") { toast.error("La firma non è stata registrata: richieda un nuovo codice e riprovi."); }
      }
    }, 3000);
    return () => clearInterval(t);
  }, [polling]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-page">
      <header className="border-b border-slate-200 bg-white"><div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3"><img src="/brand/on-logo.png" alt="ON!" className="h-8" /><span className="text-sm text-slate-500">Firma online dell'offerta</span></div></header>
      <main className="mx-auto max-w-3xl space-y-5 px-4 py-6 sm:py-10">
        {err && <section className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-900" data-testid="sign-error">{err}</section>}
        {!d && !err && <p className="text-sm text-slate-500">Caricamento…</p>}
        {d && <Summary d={d} token={token} />}
        {d && (d.status === "firmata" ? <Done d={d} token={token} /> : d.status === "in_firma" || polling ? <Waiting />
          : <SignForm d={d} token={token} onSigned={load} onPending={() => { setD({ ...d, status: "in_firma" }); setPolling(true); }} />)}
      </main>
    </div>
  );
}
